import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../i18n/I18nContext";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { Bell, LogOut, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../../lib/api";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/a8eea4b1-677b-4892-819c-00b0a2a4bc7e/images/119d03132e95e62a31040fc0dac96707989a4cabfffe8df9205b7dac9b7540b9.png";

export function TopBar() {
  const { user, logout } = useAuth();
  const t = useT();
  const nav = useNavigate();
  const loc = useLocation();
  const [unread, setUnread] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    let active = true;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (active) setUnread(data.filter((n) => !n.read).length);
      } catch {
        // ignore
      }
    };
    load();
    const intv = setInterval(load, 15000);
    return () => { active = false; clearInterval(intv); };
  }, [user, loc.pathname]);

  const linksForRole = () => {
    if (!user) return [];
    if (user.role === "admin") {
      return [
        { to: "/dashboard/admin", label: t("nav.dashboard") },
        { to: "/admin/groups", label: t("nav.groups") },
        { to: "/admin/leads", label: t("nav.leads") },
        { to: "/admin/users", label: t("nav.users") },
        { to: "/admin/payments", label: t("nav.payments") },
        { to: "/admin/auctions", label: t("nav.auctions") },
        { to: "/audit-log", label: t("nav.audit") },
      ];
    }
    if (user.role === "controller") {
      return [
        { to: "/dashboard/controlling", label: t("nav.dashboard") },
        { to: "/controlling/payments", label: t("nav.payments") },
        { to: "/controlling/auctions", label: t("nav.auctions") },
        { to: "/audit-log", label: t("nav.audit") },
        { to: "/controlling/exports", label: t("nav.exports") },
      ];
    }
    return [
      { to: "/dashboard/participant", label: t("nav.my_dashboard") },
      { to: "/my/group", label: t("nav.my_group") },
      { to: "/my/payments", label: t("nav.payments") },
      { to: "/my/auctions", label: t("nav.auctions") },
    ];
  };

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-[#E2E8E4]">
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img src={LOGO_URL} alt="Nivara Foundation" className="h-9 w-9" />
          <div className="hidden sm:block">
            <div className="font-heading text-[17px] leading-none font-semibold tracking-tight text-[#0F4C3A]">NIVARA</div>
            <div className="text-[10px] font-semibold tracking-[0.25em] text-[#94A3B8] uppercase mt-1">{t("brand.foundation")}</div>
          </div>
        </Link>

        {user && (
          <nav className="hidden lg:flex items-center gap-1">
            {linksForRole().map((l) => {
              const active = loc.pathname === l.to || loc.pathname.startsWith(l.to + "/");
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.to.split("/").pop()}`}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-all ${
                    active ? "bg-[#0F4C3A] text-white" : "text-[#475569] hover:bg-[#F8FAF9] hover:text-[#0F4C3A]"
                  }`}
                >
                  {l.label}
                </Link>
              );
            })}
          </nav>
        )}

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          {user ? (
            <>
              <Link
                to="/notifications"
                className="relative p-2 rounded-md hover:bg-[#F8FAF9] transition"
                data-testid="nav-notifications"
              >
                <Bell className="w-5 h-5 text-[#0F4C3A]" />
                {unread > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#D4AF37] text-[#0F4C3A] text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </Link>
              <div className="hidden md:flex flex-col items-end leading-tight mr-2">
                <span className="text-sm font-medium text-[#0F4C3A]">{user.name}</span>
                <span className="text-[10px] uppercase tracking-widest text-[#94A3B8]">{t(`role.${user.role}`)}</span>
              </div>
              <button
                onClick={async () => { await logout(); nav("/login"); }}
                className="p-2 rounded-md hover:bg-[#F8FAF9] transition"
                data-testid="logout-button"
                title={t("nav.logout")}
              >
                <LogOut className="w-5 h-5 text-[#0F4C3A]" />
              </button>
              <button
                className="lg:hidden p-2 rounded-md hover:bg-[#F8FAF9]"
                onClick={() => setMobileOpen(!mobileOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-link" className="px-4 py-2 text-sm font-medium text-[#0F4C3A] hover:underline">
                {t("nav.login")}
              </Link>
              <button
                onClick={() => {
                  if (loc.pathname === "/") {
                    document.getElementById("interest")?.scrollIntoView({ behavior: "smooth" });
                  } else {
                    nav("/#interest");
                    setTimeout(() => document.getElementById("interest")?.scrollIntoView({ behavior: "smooth" }), 80);
                  }
                }}
                data-testid="nav-register-link"
                className="nv-btn-primary px-4 py-2 text-sm font-medium rounded-md"
              >
                {t("interest.cta_anchor")}
              </button>
            </>
          )}
        </div>
      </div>
      {user && mobileOpen && (
        <div className="lg:hidden bg-white border-t border-[#E2E8E4] px-4 py-3">
          <div className="flex items-center justify-between px-3 py-3 mb-2 border-b border-[#E2E8E4]">
            <span className="nv-label">Sprache · Language</span>
            <LanguageSwitcher />
          </div>
          {linksForRole().map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setMobileOpen(false)}
              className="block px-3 py-2 text-sm text-[#0F4C3A] hover:bg-[#F8FAF9] rounded"
              data-testid={`mobile-nav-${l.to.split("/").pop()}`}
            >
              {l.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}
